"use client";

import React, { useEffect, useRef, useState } from "react";
import Script from "next/script";
import { useAppContext } from "@components/Provider";
import { RenderHomePageSchema } from "helpers/Helper";
import EBBLandingSignupForm from "./EBBLandingSignupForm";
import {
  ArtImage,
  LandingHeader,
  LandingSignupModal,
  Testimonials,
  renderAccentText,
} from "./EBBLandingShared";
import {
  extractHomepageClosing,
  extractHomepageFeatures,
  extractHomepageSignup,
  extractHomepageTestimonials,
  extractInstructionalSection,
  extractSimpleAboutArt,
  extractSimpleLandingMedia,
  getLandingPagePost,
} from "./ebbLandingPageUtils";
import styles from "./EBBLandingPageSimple.module.css";

const renderHeroHeadline = (text) => {
  const lines = String(text || "")
    .split(/\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (lines.length < 2) {
    return renderAccentText(text, styles.heroEm);
  }
  return lines.map((line, index) => (
    <span key={`hero-line-${index}`} className={index === 0 ? styles.heroLine : styles.heroEm}>
      {renderAccentText(line, styles.heroEm)}
    </span>
  ));
};

const EBBLandingPageSimple = (props) => {
  const { data, host: contextHost, siteId: contextSiteId } = useAppContext();
  const post = getLandingPagePost(props);
  const host = contextHost || props.host;
  const siteId = contextSiteId || props.siteId;
  const siteSettings = data?.SiteSettings || {};
  const {
    header: signupHeader,
    subcopy: signupSubcopy,
    modalHeader: signupModalHeader,
    buttonText: ctaText,
    signUpLocation,
    pageNewsletter,
    isModal: isModalSignup,
    newsletterProps,
  } = extractHomepageSignup(post);
  const rotationNow = props.heroRotationNow ? new Date(props.heroRotationNow) : new Date();
  const { devices, alt: devicesAlt } = extractSimpleLandingMedia(post, rotationNow);
  const { header: aboutHeader } = extractInstructionalSection(post);
  const features = extractHomepageFeatures(post);
  const aboutArt = extractSimpleAboutArt(post);
  const hasAboutArt = Boolean(aboutArt.desktop || aboutArt.mobile);
  const quotes = extractHomepageTestimonials(post);
  const closing = extractHomepageClosing(post);
  const siteLogo =
    siteSettings.primary_site_logo_svg_black ||
    siteSettings.primary_site_logo_svg ||
    siteSettings.primary_site_logo;
  const siteName = siteSettings.site_name || "Early Bird Books";
  const heroRef = useRef(null);
  const closingRef = useRef(null);
  const [heroPast, setHeroPast] = useState(false);
  const [closingInView, setClosingInView] = useState(false);
  const [signupModal, setSignupModal] = useState(null);
  const showStickySignup = heroPast && !closingInView;

  useEffect(() => {
    const hero = heroRef.current;
    const closing = closingRef.current;
    if (!hero) return undefined;

    let frame = 0;

    const update = () => {
      frame = 0;
      const headerRow = document.querySelector("[data-ebb-landing-header-row]");
      const headerBottom = headerRow ? headerRow.getBoundingClientRect().bottom : 64;
      const pastHero = hero.getBoundingClientRect().bottom <= headerBottom + 8;
      const closingTop = closing ? closing.getBoundingClientRect().top : Infinity;
      const arrivedAtClosing = closingTop < Math.max(headerBottom + 48, window.innerHeight * 0.4);
      setHeroPast(pastHero);
      setClosingInView(arrivedAtClosing);
    };

    const scheduleUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate);

    const resizeObserver = new ResizeObserver(scheduleUpdate);
    resizeObserver.observe(hero);
    resizeObserver.observe(document.documentElement);
    if (closing) resizeObserver.observe(closing);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
      resizeObserver.disconnect();
    };
  }, []);

  const scrollToSignup = () => {
    const target = document.getElementById("landing-simple-signup");
    target?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const onCtaClick = () => {
    if (isModalSignup) {
      setSignupModal("page");
      return;
    }
    scrollToSignup();
  };

  return (
    <div className={styles.page}>
      <LandingHeader
        siteLogo={siteLogo}
        siteName={siteName}
        showStickySignup={showStickySignup}
        onCtaClick={onCtaClick}
        headline={signupHeader}
        ctaText={ctaText}
        signUpLocation={signUpLocation}
        pageNewsletter={pageNewsletter}
        Newsletter_Settings={siteSettings.newsletter_settings}
        postType={post?.post_type}
        stickyFormId="landing-simple-sticky-form"
        headlineAccentClass={styles.accent}
      />

      <section className={styles.heroBleed} ref={heroRef} data-ebb-hero-card="true">
        <div className={styles.hero}>
          <div className={styles.heroCopy}>
            {signupHeader ? <h1>{renderHeroHeadline(signupHeader)}</h1> : null}
            {signupSubcopy ? <p className={styles.heroSub}>{signupSubcopy}</p> : null}
            <EBBLandingSignupForm
              variant="hero"
              formId="landing-simple-hero-form"
              signUpLocation={signUpLocation}
              newsletterProps={pageNewsletter}
              Newsletter_Settings={siteSettings.newsletter_settings}
              postType={post?.post_type}
              layoutClass="signupForm_simpleHero"
            />
          </div>
          {devices ? (
            <div className={styles.heroDevices}>
              <img
                src={devices}
                alt={devicesAlt || ""}
                className={styles.devicesImage}
                fetchPriority="high"
              />
            </div>
          ) : null}
        </div>
      </section>

      {features.length > 0 || hasAboutArt ? (
        <div className={styles.band}>
          <section className={`${styles.about} ${hasAboutArt ? styles.hasMosaic : ""}`}>
            {hasAboutArt ? (
              <div className={styles.mosaic}>
                <ArtImage
                  desktopSrc={aboutArt.desktop}
                  mobileSrc={aboutArt.mobile}
                  className={styles.mosaicImage}
                  alt={aboutArt.alt || ""}
                />
              </div>
            ) : null}
            <div className={styles.aboutCopy}>
              {aboutHeader ? <h2>{renderAccentText(aboutHeader, styles.accent)}</h2> : null}
              {features.map((feature, index) => (
                <div className={styles.aboutBlock} key={`about-${index}`}>
                  {feature.heading ? (
                    <h3>{renderAccentText(feature.heading, styles.accent)}</h3>
                  ) : null}
                  {feature.body ? <p>{feature.body}</p> : null}
                </div>
              ))}
              {ctaText ? (
                <button type="button" className={styles.aboutCta} onClick={onCtaClick}>
                  {ctaText}
                </button>
              ) : null}
            </div>
          </section>
        </div>
      ) : null}

      {quotes.length > 0 ? (
        <div className={styles.band}>
          <Testimonials
            quotes={quotes}
            classes={{
              section: styles.testimonials,
              desktop: styles.testimonialsDesktop,
              mobile: styles.testimonialsMobile,
              card: styles.testimonialCard,
            }}
          />
        </div>
      ) : null}

      <div className={styles.band}>
        <section className={styles.closing} id="landing-simple-signup" ref={closingRef}>
          <ArtImage
            desktopSrc={closing.imageDesktop}
            mobileSrc={closing.imageMobile}
            className={styles.closingBanner}
            alt=""
          />
          {closing.header ? <h2>{renderAccentText(closing.header, styles.closingEm)}</h2> : null}
          <EBBLandingSignupForm
            variant="closing"
            formId="landing-simple-closing-form"
            signUpLocation={signUpLocation}
            newsletterProps={pageNewsletter}
            Newsletter_Settings={siteSettings.newsletter_settings}
            postType={post?.post_type}
            layoutClass="signupForm_simpleClosing"
          />
        </section>
      </div>

      {isModalSignup ? (
        <LandingSignupModal
          isOpen={signupModal === "page"}
          onClose={() => setSignupModal(null)}
          formId="landing-simple-modal-form"
          headline={signupModalHeader || signupHeader}
          signUpLocation={signUpLocation}
          newsletterProps={newsletterProps}
          Newsletter_Settings={siteSettings.newsletter_settings}
          postType={post?.post_type}
        />
      ) : null}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={RenderHomePageSchema(siteSettings, post, host, siteId)}
      />
      <Script
        strategy="lazyOnload"
        type="text/javascript"
        data-cfasync="false"
        dangerouslySetInnerHTML={{
          __html: `
            var blogherads = blogherads || {};
            blogherads.adq = blogherads.adq || [];
            blogherads.adq.push(function() {
              blogherads.disableAds();
            });
          `,
        }}
      />
    </div>
  );
};

export default EBBLandingPageSimple;
